import { supabase } from '../lib/supabase'
import {
  OWN_DELIVERY_METHOD_ID,
  OWN_DELIVERY_SETTING_KEY,
} from '../constants/ownDelivery'
import type {
  SendcloudCreateShipmentData,
  SendcloudServiceResponse,
  SendcloudShippingOption,
} from '../types/sendcloud'
import {
  getLmdSetting,
  updateLmdSetting,
} from './lmdRatesService'

export async function isOwnDeliveryEnabled(): Promise<boolean> {
  try {
    const setting = await getLmdSetting(OWN_DELIVERY_SETTING_KEY)
    if (!setting) return false
    return Number(setting.setting_value) === 1
  } catch (err) {
    console.error('Error reading own delivery setting:', err)
    return false
  }
}

export async function setOwnDeliveryEnabled(enabled: boolean): Promise<void> {
  const { data, error } = await supabase
    .from('lmd_settings')
    .select('id, setting_value')
    .eq('setting_key', OWN_DELIVERY_SETTING_KEY)
    .maybeSingle()

  if (error) {
    console.error('Error loading own delivery setting:', error)
    throw error
  }

  if (!data) {
    const { error: insertError } = await supabase.from('lmd_settings').insert({
      setting_key: OWN_DELIVERY_SETTING_KEY,
      setting_value: enabled ? 1 : 0,
      setting_type: 'boolean',
      description:
        'Show E-Freight Own Delivery as a shipping option (local labels, no Sendcloud / no T&T). Uses LMD rates for invoicing.',
      is_active: true,
    })
    if (insertError) throw insertError
    return
  }

  await updateLmdSetting(data.id, {
    setting_value: enabled ? 1 : 0,
    is_active: true,
  })
}

export function buildOwnDeliveryShippingOption(
  countryCode: string,
): SendcloudShippingOption {
  const iso = (countryCode || 'NL').toUpperCase()
  return {
    id: OWN_DELIVERY_METHOD_ID,
    name: 'Own Delivery',
    carrier: 'efreight',
    min_weight: '0.001',
    max_weight: '9999.0',
    service_point_input: 'none',
    price: 0,
    countries: [
      {
        id: 0,
        name: iso,
        price: 0,
        iso_2: iso,
        iso_3: iso,
        lead_time_hours: null,
      },
    ],
  }
}

export interface CreateOwnDeliveryRequest {
  bookingId: number
  palletCount: number
  addressOverrides?: {
    name?: string
    company_name?: string
    address?: string
    house_number?: string
    city?: string
    postal_code?: string
    country?: string
    email?: string
    telephone?: string
  }
}

export async function createOwnDeliveryShipments(
  request: CreateOwnDeliveryRequest,
): Promise<SendcloudServiceResponse<SendcloudCreateShipmentData>> {
  try {
    const { data, error } = await supabase.functions.invoke('sendcloud-shipment', {
      body: {
        action: 'create-own-delivery',
        bookingId: request.bookingId,
        palletCount: request.palletCount,
        addressOverrides: request.addressOverrides,
      },
    })

    if (error) {
      console.error('Own delivery edge function error:', error)
      return { success: false, error: error.message }
    }

    return data as SendcloudServiceResponse<SendcloudCreateShipmentData>
  } catch (err) {
    console.error('Own delivery create error:', err)
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error occurred',
    }
  }
}
